/// <amd-module name="Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownDataManager" />

import { TeamMemberCapacity, TeamSettingsDaysOff } from "Dashboards/Clients/contracts/Work";
import { WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import { DemandType } from "Reporting/Visuals/LayoutComponents/Widgets/Demands/DemandType";
import { LayoutState, MessageType } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { DayOfWeek, fetchTodayInAccountTimeZone } from "WidgetCommon/DateUtils/DateUtils";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { WidgetDataManagerOptions } from "WidgetCommon/ModernWidgetTypes/WidgetDataManagerBase";
import { AnalyticsSprintBurndownChartOptionsFactory } from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownChartOptionsFactory";
import { AsyncDataHelpers } from "Widgets/Burndown/BurndownWidgetComponents/AsyncDataHelpers";
import { BurndownDataFactory } from "Widgets/Burndown/BurndownWidgetComponents/BurndownDataFactory";
import { BurndownDataManager } from "Widgets/Burndown/BurndownWidgetComponents/BurndownDataManager";
import { DateSamplingConfiguration } from "Widgets/Burndown/BurndownWidgetComponents/BurndownSettings";
import { TimeIntervalUtils } from "Widgets/Burndown/BurndownWidgetComponents/TimeIntervalUtils";
import * as WidgetResources from "Widgets/Burndown/Resources";
import { AnalyticsSprintBurndownSettings } from "./AnalyticsSprintBurndownSettings";
import {
    AnalyticsSprintBurndownSettingsManager,
    convertSprintBurndownToBurndownSettings,
    mapSymbolicIterationToConcrete
} from "./AnalyticsSprintBurndownSettingsManager";
import { isSymbolicCurrentIteration } from "./ConfigQueries/IterationsHelper";
import { TeamCapacityCacheableQuery } from "./TfsQueries/TeamCapacityCacheableQuery";
import { TeamDaysOffCacheableQuery } from "./TfsQueries/TeamDaysOffCacheableQuery";
import { TeamWorkingDaysCacheableQuery } from "./TfsQueries/TeamWorkingDaysCacheableQuery";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { BurndownConstants } from "Widgets/Burndown/BurndownWidgetComponents/BurndownConstants";

export class AnalyticsSprintBurndownDataManager extends BurndownDataManager {
    protected isAutomationScenario: boolean;
    private AnalyticsSprintBurndownDataManagerPageContext: IVssPageContext;

    constructor(
        pageContext: IVssPageContext,
        teamContext: WidgetTeamContext,
        options: WidgetDataManagerOptions,
        allowChartClicks: boolean,
        commandName: string,
        isAutomationScenario?: boolean
    ) {
        const settingsManager = undefined;
        super(pageContext, teamContext, options, allowChartClicks, commandName, settingsManager, isAutomationScenario);
        // We're coercing the type here. The settings manager interface should be agnostic of settings format internals, by moving to a black box props format.
        this.settingsManager = new AnalyticsSprintBurndownSettingsManager(pageContext, teamContext) as any;
        this.isAutomationScenario = isAutomationScenario === true;
        this.AnalyticsSprintBurndownDataManagerPageContext = pageContext;
    }

    public demandsArePresent(): boolean {
        return (
            this.demandTracker.isDemandPresent(DemandType.subtitle) ||
            this.demandTracker.isDemandPresent(DemandType.scalar) ||
            this.demandTracker.isDemandPresent(DemandType.chart) ||
            this.demandTracker.isDemandPresent(DemandType.submetrics)
        );
    }

    protected async getDataImpl(): Promise<LayoutState> {
        try {
            if (this.settings == undefined) {
                throw new Error(WidgetResources.BurndownWidgetDataManager_NoWidgetSettingsFound);
            }

            let sprintBurndownSettings = JSON.parse(this.settings) as AnalyticsSprintBurndownSettings;

            // override value with 'false' if FF is disabled
            sprintBurndownSettings.showResolvedItemsAsCompletedEnabled = isFeatureFlagEnabled(
                this.AnalyticsSprintBurndownDataManagerPageContext,
                BurndownConstants.featureFlagName_ShowResolvedItemsAsCompleted, 
                false) ? sprintBurndownSettings.showResolvedItemsAsCompletedEnabled : false;

            // If currentIteration was configured, we need to map this to real iteration information
            if (isSymbolicCurrentIteration(sprintBurndownSettings.iterationId)) {
                sprintBurndownSettings = await mapSymbolicIterationToConcrete(this.context, sprintBurndownSettings);
            }

            // Settings down-converted to the release Burndown settings format, for behaviors handled by the common implementation.
            // This is a 1-way conversion - sprint burndown settings are more restrictive than Release burndown.
            // Some sprint burndown only settings are not passed down. These are handled directly by the sprint Burndown layer (particularly the chart Options factory code)
            const releaseBurndownCoreSettings = convertSprintBurndownToBurndownSettings(sprintBurndownSettings);

            const projectIds = [sprintBurndownSettings.team.projectId];
            const teamId = sprintBurndownSettings.team.teamId;

            // Retrieve the work item types and fields
            const workItemTypesPromise = AsyncDataHelpers.getWorkItemTypesForTeam(
                this.context,
                sprintBurndownSettings.workItemTypeFilter,
                teamId,
                this.isAutomationScenario
            );
            const workItemTypesFieldsPromise = workItemTypesPromise.then(workItemTypes =>
                AsyncDataHelpers.getWorkItemTypeFieldsPromise(
                    this.context,
                    workItemTypes,
                    projectIds,
                    this.dataService,
                    this.isAutomationScenario,
                    this.command
                )
            );

            return Promise.all<any>([workItemTypesPromise, workItemTypesFieldsPromise])
                .then(([workItemTypes, workItemTypesFields]) => {
                    let chartAndSubmetricUpdatedPromise;
                    if (this.demandTracker.isDemandPresent(DemandType.chart) || this.demandTracker.isDemandPresent(DemandType.submetrics)) {
                        const sampleDatesStructure = TimeIntervalUtils.getSampleDates(
                            sprintBurndownSettings.timePeriodConfiguration.startDate,
                            releaseBurndownCoreSettings.timePeriodConfiguration.samplingConfiguration.settings as DateSamplingConfiguration
                        );

                        const effortSnapshotsPromise = this.getEffortSnapshotsPromise(
                            releaseBurndownCoreSettings,
                            workItemTypes,
                            workItemTypesFields,
                            sampleDatesStructure
                        );
                        const currentEffortPromise = this.getCurrentEffortPromise(releaseBurndownCoreSettings, workItemTypes, workItemTypesFields);

                        let unestimatedCountQuery;
                        if (sprintBurndownSettings.aggregation.identifier === AggregationMode.Sum) {
                            unestimatedCountQuery = this.getUnestimatedCountPromise(releaseBurndownCoreSettings, workItemTypes, workItemTypesFields);
                        }

                        let teamdaysOffPromise;
                        if (sprintBurndownSettings.iterationId) {
                            teamdaysOffPromise = this.getTeamDaysOffPromise(teamId, sprintBurndownSettings.iterationId);
                        } else {
                            throw new Error("Iteration for widget is not set.");
                        }

                        const teamWorkingDaysPromise = this.getTeamWorkingDaysPromise(teamId);

                        const teamCapacityPromise = this.getTeamCapacity(teamId, sprintBurndownSettings.iterationId);

                        const witColorDictionaryPromise = this.getWitColorDictionaryPromise(
                            this.context,
                            sprintBurndownSettings.stackByWorkItemTypeEnabled,
                            workItemTypes
                        );

                        // Pass date interval as we're not using iterations
                        const sampleDatesInterval = (releaseBurndownCoreSettings.timePeriodConfiguration.samplingConfiguration
                            .settings as DateSamplingConfiguration).sampleInterval;
                        const todayInAccountTimeZonePromise = fetchTodayInAccountTimeZone(this.context);

                        chartAndSubmetricUpdatedPromise = Promise.all<any>([
                            effortSnapshotsPromise,
                            currentEffortPromise,
                            unestimatedCountQuery,
                            teamdaysOffPromise,
                            teamWorkingDaysPromise,
                            teamCapacityPromise,
                            witColorDictionaryPromise,
                            todayInAccountTimeZonePromise
                        ]).then(
                            async ([
                                effortSnapshots,
                                currentEffort,
                                unestimatedCount,
                                teamdaysOff,
                                teamWorkingDays,
                                teamCapacityData,
                                witColorDictionary,
                                todayInAccountTimeZone
                            ]) => {
                                // Empty chart experience when we have no data. If we have unestimated count data, proceed as normal since
                                // we want to show you that useful submetric.
                                if (effortSnapshots.length === 0 && unestimatedCount == null) {
                                    return this.packMessageAsState(MessageType.NoData);
                                } else {
                                    // Ensure any prior messaging is cleared out.
                                    this.resetMessageState();
                                }

                                const pastDays = sampleDatesStructure.sampleDates.filter(
                                    date => localeIgnoreCaseComparer(date, todayInAccountTimeZone) <= 0
                                );

                                const remainingSnapshotEffort = BurndownDataFactory.getRemainingEffort(effortSnapshots, sprintBurndownSettings.showResolvedItemsAsCompletedEnabled);
                                const completedSnapshotEffort = BurndownDataFactory.getCompletedEffort(effortSnapshots, sprintBurndownSettings.showResolvedItemsAsCompletedEnabled);
                                const datesToTotalSnapshotEffort = BurndownDataFactory.getEffortByDate(effortSnapshots);
                                const datesToEffortForMainSeries = this.resolvePastDays(
                                    pastDays,
                                    remainingSnapshotEffort,
                                    completedSnapshotEffort,
                                    datesToTotalSnapshotEffort
                                );

                                const averageBurn = -BurndownDataFactory.getAverageChange(datesToEffortForMainSeries, pastDays);
                                const completedPercentage = BurndownDataFactory.getCompletedPercentage(currentEffort, sprintBurndownSettings.showResolvedItemsAsCompletedEnabled);
                                const scopeChange =
                                    BurndownDataFactory.getSumOfAggregatedEffort(currentEffort) - datesToTotalSnapshotEffort[pastDays[0]];
                                const allowChartClicks = this.allowChartClicks;

                                const showFillAsArea = true; // Legacy consistency - Sprint Burndown is rendered as a continuous area chart, whereas Burndown was rendering by default as discrete columns.

                                // Suppress the total scope trendline, when "remaining work" is selected, as work goes to 0 on completion, making it identical to the trendline.
                                // This pre-empts the total scope widget setting.
                                const remainingWorkField = "Microsoft.VSTS.Scheduling.RemainingWork";
                                const showTotalScopeTrendline =
                                    !sprintBurndownSettings.isMinimalChart &&
                                    (sprintBurndownSettings.aggregation.identifier === AggregationMode.Count ||
                                        sprintBurndownSettings.aggregation.settings !== remainingWorkField) &&
                                    sprintBurndownSettings.totalScopeTrendlineEnabled;

                                const chartOptions = new AnalyticsSprintBurndownChartOptionsFactory(this.context).createChartOptions({
                                    todayInAccountTimezone: todayInAccountTimeZone,
                                    showOtherEffort: sprintBurndownSettings.completedWorkEnabled,
                                    showScopeTrendline: showTotalScopeTrendline,
                                    showBurnTrendline: false, // Removed from Sprint Burndown.
                                    showResolvedItemsAsCompleted: sprintBurndownSettings.showResolvedItemsAsCompletedEnabled,
                                    sampleDatesInterval: sampleDatesInterval,
                                    lastGeneratedIntervalSampleDate: sampleDatesStructure.lastIntervalDate,
                                    sampleDates: sampleDatesStructure.sampleDates,
                                    datesToTotalEffort: datesToTotalSnapshotEffort,
                                    remainingEffort: remainingSnapshotEffort,
                                    completedEffort: completedSnapshotEffort,
                                    startDate: sprintBurndownSettings.timePeriodConfiguration.startDate,
                                    suppressAnimations: this.suppressAnimations,
                                    showStackedWorkItemTypes: sprintBurndownSettings.stackByWorkItemTypeEnabled,
                                    workItemTypeColorDictionary: witColorDictionary,
                                    averageBurn: averageBurn,
                                    datesToPrimarySeriesEffort: datesToEffortForMainSeries,
                                    onClick: this.getOnClickChartHandler(releaseBurndownCoreSettings, workItemTypes, workItemTypesFields, undefined),
                                    burnDirection: this.getBurnDirection(),
                                    allowChartClicks: allowChartClicks,
                                    showFillAsArea: showFillAsArea,
                                    skipFutureScopeInTrendLine: true,

                                    // AX Burndown specific Options
                                    showNonWorkingDays: sprintBurndownSettings.showNonWorkingDays,
                                    showRemainingCapacityAtStartOfDay: sprintBurndownSettings.showRemainingCapacityAtStartOfDay,
                                    teamDaysOff: teamdaysOff,
                                    teamWorkingDays: teamWorkingDays,
                                    teamMemberCapacities: teamCapacityData,
                                    isMinimalChart: sprintBurndownSettings.isMinimalChart
                                });

                                this.currentState.chartData = {
                                    chartOptions: chartOptions
                                };

                                // SubMetrics
                                this.currentState.submetricsData = this.getSubMetrics(
                                    releaseBurndownCoreSettings,
                                    workItemTypes,
                                    workItemTypesFields,
                                    completedPercentage,
                                    allowChartClicks,
                                    averageBurn,
                                    scopeChange
                                );

                                this.updateHeaderAndMetrics(
                                    unestimatedCount,
                                    sampleDatesStructure.sampleDates,
                                    releaseBurndownCoreSettings,
                                    workItemTypes,
                                    workItemTypesFields,
                                    allowChartClicks
                                );
                                return Promise.resolve();
                            }
                        );
                    }
                    return this.handleScalarAndTitlePromise(
                        chartAndSubmetricUpdatedPromise,
                        releaseBurndownCoreSettings,
                        workItemTypes,
                        workItemTypesFields
                    );
                })
                .then(undefined /* Catch pattern */, error => {
                    return this.handleError(error);
                });
        } catch (error) {
            return this.handleError(error);
        }
    }

    private async getTeamDaysOffPromise(teamId: string, iterationId: string): Promise<TeamSettingsDaysOff> {
        const teamdaysOffQuery = new TeamDaysOffCacheableQuery(this.context, teamId, iterationId);

        return this.dataService.getCacheableQueryResult(teamdaysOffQuery);
    }

    private async getTeamWorkingDaysPromise(teamId: string): Promise<DayOfWeek[]> {
        const teamWorkingDaysQuery = new TeamWorkingDaysCacheableQuery(this.context, teamId);

        return this.dataService.getCacheableQueryResult(teamWorkingDaysQuery);
    }

    private async getTeamCapacity(teamId: string, iterationId: string): Promise<TeamMemberCapacity[]> {
        const teamCapacityQuery = new TeamCapacityCacheableQuery(this.context, teamId, iterationId);
        return this.dataService.getCacheableQueryResult(teamCapacityQuery);
    }
}
